import React, { createContext, useContext, useState, useMemo } from 'react';
import energyIcon from '../assets/icons/power-plant-icon.png';
import fireStationIcon from '../assets/icons/layers-icons/fire-station.png';
import hospitalIcon from '../assets/icons/layers-icons/hospital.png';
import biomassIcon from '../assets/icons/layers-icons/biomass.png';
import coalIcon from '../assets/icons/layers-icons/coal.png';
import cogenerationIcon from '../assets/icons/layers-icons/Cogeneration.png';
import gasIcon from '../assets/icons/layers-icons/gas.png';
import geothermalIcon from '../assets/icons/layers-icons/Geothermal.png';
import hydroIcon from '../assets/icons/layers-icons/hydro.png';
import nuclearIcon from '../assets/icons/layers-icons/nuclear.png';
import oilIcon from '../assets/icons/layers-icons/oil.png';
import petrocoke from '../assets/icons/layers-icons/Petroleum-coke.png';
import solarIcon from '../assets/icons/layers-icons/solar.png';
import storageIcon from '../assets/icons/layers-icons/storage.png';
import waste from '../assets/icons/layers-icons/waste.png';
import wavetidalIcon from '../assets/icons/layers-icons/wave-and-tidal.png';
import windIcon from '../assets/icons/layers-icons/wind.png';
import Papa from 'papaparse';
import { Popup } from 'mapbox-gl';

const SensorDataContext = createContext();

export const FacilitiesContextProvider = ({ children }) => {
    const returnLayerIcon = (id) => {
        switch (id) {
            case 'Layer-powerplants-Solar-symbol':
                return solarIcon;
            case 'Layer-powerplants-Biomass-symbol':
                return biomassIcon;
            case 'Layer-powerplants-Coal-symbol':
                return coalIcon;
            case 'Layer-powerplants-Cogeneration-symbol':
                return cogenerationIcon;
            case 'Layer-powerplants-Gas-symbol':
                return gasIcon;
            case 'Layer-powerplants-Geothermal-symbol':
                return geothermalIcon;
            case 'Layer-powerplants-Hydro-symbol':
                return hydroIcon;
            case 'Layer-powerplants-Nuclear-symbol':
                return nuclearIcon;
            case 'Layer-powerplants-Oil-symbol':
                return oilIcon;
            case 'Layer-powerplants-Petcoke-symbol':
                return petrocoke;
            case 'Layer-powerplants-Storage-symbol':
                return storageIcon;
            case 'Layer-powerplants-Waste-symbol':
                return waste;
            case 'Layer-powerplants-Wave and Tidal-symbol':
                return wavetidalIcon;
            case 'Layer-powerplants-Wind-symbol':
                return windIcon;
            case 'Layer-emergency-stations-Fire Station-symbol':
                return fireStationIcon;
            case 'Layer-emergency-stations-EMS-symbol':
                return hospitalIcon;
            default:
                return energyIcon;
        }
    };
 
    const loadCSV = (filePath) => {
        return new Promise((resolve, reject) => {
            Papa.parse(filePath, {
                download: true,
                header: true,
                complete: (result) => resolve(result.data),
                error: (error) => reject(error),
            });
        });
    };

    const getPowerPlants = async (map) => {
        try {
            const plants = await loadCSV(`${process.env.PUBLIC_URL}/global-powerplants.csv`);
            plotPowerPlants(map, plants);
        }
        catch (error) {
            console.error('Error loading energy stations:', error);
        }
    }
    
    const plotPowerPlants = async (map, stations) => {
        if (!map?.current) {
            console.warn('plotPowerPlants: Map not initialized, skipping plot');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('plotPowerPlants: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                plotPowerPlants(map, stations);
            });
            return;
        }
        const powerPlantTypes = new Map();

        stations.forEach((station) => {
            if (!powerPlantTypes.has(station.primary_fuel)) {
                powerPlantTypes.set(station.primary_fuel, [station]);
            } else {
                powerPlantTypes.set(station.primary_fuel, [...powerPlantTypes.get(station.primary_fuel), station])
            }
        })

        const stationsArray = Array.from(powerPlantTypes);

        stationsArray.forEach((station) => {
            try {
                const layers = map.current.getStyle().layers.filter(
                    (layer) => layer.id === `Layer-powerplants-${station[0]}-symbol`
                );
        
                if (layers.length > 0) layers.forEach((layer) => map.current.removeLayer(layer.id));
        
                const source = map.current.getStyle().sources[`Source-powerplants-${station[0]}`];
                if (source) map.current.removeSource(`Source-powerplants-${station[0]}`);
        
                if (map.current.hasImage(`${station[0]}-icon`)) {
                    map.current.removeImage(`${station[0]}-icon`);
                }
        
                map.current.loadImage(returnLayerIcon(`Layer-powerplants-${station[0]}-symbol`), function (error, image) {
                    if (error) throw error;
        
                    map.current.addImage(`${station[0]}-icon`, image);
    
                    const geojsonData = {
                        type: 'FeatureCollection',
                        features: station[1].map((s) => ({
                            type: 'Feature',
                            properties: s,
                            geometry: {
                                type: 'Point',
                                coordinates: [parseFloat(s.longitude), parseFloat(s.latitude)],
                            },
                        })),
                    };
        
                    map.current.addSource(`Source-powerplants-${station[0]}`, {
                        type: 'geojson',
                        data: geojsonData,
                    });
        
                    map.current.addLayer({
                        id: `Layer-powerplants-${station[0]}-symbol`,
                        type: 'symbol',
                        source: `Source-powerplants-${station[0]}`,
                        layout: {
                            'icon-image': `${station[0]}-icon`,
                            'icon-size': 0.45,
                            visibility: 'none',
                        },
                    });
        
                    map.current.on('click', `Layer-powerplants-${station[0]}-symbol`, (e) => {
                        const features = map.current.queryRenderedFeatures(e.point, {
                            layers: [`Layer-powerplants-${station[0]}-symbol`],
                        });
        
                        if (!features.length) return;
        
                        const feature = features[0];
                        const location = [feature.geometry.coordinates[0], feature.geometry.coordinates[1]];
        
                        map.current.flyTo({ center: location, zoom: 17, pitch: 0, offset: [250, 0] });
        
                        const properties = feature.properties;
    
                        const existingPopups = document.getElementsByClassName('popup');
                        Array.from(existingPopups).forEach((popup) => popup.remove());
    
                        new Popup({ offset: 25, maxWidth: '700px', className: 'popup' })
                            .setLngLat(location)
                            .setHTML(`<div>
                            <div class='alert-popup-title'>${properties.primary_fuel || 'Unknown'} Energy Station</div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Country:</div>
                                <div class='alert-popup-row-value'>${properties.country_long || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Name:</div>
                                <div class='alert-popup-row-value'>${properties.name || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Coordinates:</div>
                                <div class='alert-popup-row-value'>${properties.latitude || 'Unknown'}, ${properties.longitude || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Owner:</div>
                                <div class='alert-popup-row-value'>${properties.owner|| 'Unknown'}</div>
                            </div>
                        </div>`)
                            .addTo(map.current);
                    });
        
                    map.current.on('mouseenter', `Layer-powerplants-${station[0]}-symbol`, () => {
                        map.current.getCanvas().style.cursor = 'pointer';
                    });
        
                    map.current.on('mouseleave', `Layer-powerplants-${station[0]}-symbol`, () => {
                        map.current.getCanvas().style.cursor = '';
                    });
                });
            } catch (error) {
                console.error('Error plotting energy stations:', error);
            }
        })
    };

    const getEmergencyStations = async (map) => {
        try {
            const stations = await loadCSV(`${process.env.PUBLIC_URL}/emergency-stations.csv`);
            plotEmergencyStations(map, stations);
        }
        catch (error) {
            console.error('Error loading energy stations:', error);
        }
    }

    const plotEmergencyStations = async (map, emergencyStations) => {
        if (!map?.current) {
            console.warn('plotEmergencyStations: Map not initialized, skipping plot');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('plotEmergencyStations: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                plotEmergencyStations(map, emergencyStations);
            });
            return;
        }
        const emergencyStationTypes = new Map([
            ['Fire Station', []],
            ['EMS', []],
        ]);
        
        emergencyStations.forEach((station) => {
            if (
                station.name &&
                (station.name.toLowerCase().includes('fire') ||
                 station.name.toLowerCase().includes('bomberos') ||
                 station.name.toLowerCase().includes('fuego'))
            ) {
                emergencyStationTypes.set('Fire Station', [...emergencyStationTypes.get('Fire Station'), station]);
            } else if (
                station.name &&
                (!station.name.toLowerCase().includes('fire') &&
                 !station.name.toLowerCase().includes('bomberos') &&
                 !station.name.toLowerCase().includes('fuego'))
            ) {
                emergencyStationTypes.set('EMS', [...emergencyStationTypes.get('EMS'), station]);
            }
        });
                
        const emergencyStationArray = Array.from(emergencyStationTypes);

        emergencyStationArray.forEach((station) => {
            try {
                const layers = map.current.getStyle().layers.filter(
                    (layer) => layer.id === `Layer-emergency-stations-${station[0]}-symbol`
                );
        
                if (layers.length > 0) layers.forEach((layer) => map.current.removeLayer(layer.id));
        
                const source = map.current.getStyle().sources[`Source-emergency-stations-${station[0]}`];
                if (source) map.current.removeSource(`Layer-emergency-stations-${station[0]}-symbol`);
        
                if (map.current.hasImage(`${station[0]}-icon`)) {
                    map.current.removeImage(`${station[0]}-icon`);
                }
        
                map.current.loadImage(returnLayerIcon(`Layer-emergency-stations-${station[0]}-symbol`), function (error, image) {
                    if (error) throw error;
        
                    map.current.addImage(`${station[0]}-icon`, image);
        
                    const geojsonData = {
                        type: 'FeatureCollection',
                        features: station[1].map((s) => ({
                            type: 'Feature',
                            properties: s,
                            geometry: {
                                type: 'Point',
                                coordinates: [parseFloat(s.longitude), parseFloat(s.latitude)],
                            },
                        })),
                    };
        
                    map.current.addSource(`Source-emergency-stations-${station[0]}`, {
                        type: 'geojson',
                        data: geojsonData,
                    });
        
                    map.current.addLayer({
                        id: `Layer-emergency-stations-${station[0]}-symbol`,
                        type: 'symbol',
                        source: `Source-emergency-stations-${station[0]}`,
                        layout: {
                            'icon-image': `${station[0]}-icon`,
                            'icon-size': 0.45,
                            visibility: 'none',
                        },
                    });
        
                    map.current.on('click', `Layer-emergency-stations-${station[0]}-symbol`, (e) => {
                        const features = map.current.queryRenderedFeatures(e.point, {
                            layers: [`Layer-emergency-stations-${station[0]}-symbol`],
                        });
        
                        if (!features.length) return;
        
                        const feature = features[0];
                        const location = [feature.geometry.coordinates[0], feature.geometry.coordinates[1]];
        
                        map.current.flyTo({ center: location, zoom: 17, pitch: 0, offset: [250, 0] });
        
                        const properties = feature.properties;
        
                        const existingPopups = document.getElementsByClassName('popup');
                        Array.from(existingPopups).forEach((popup) => popup.remove());
        
                        new Popup({ offset: 25, maxWidth: '700px', className: 'popup' })
                            .setLngLat(location)
                            .setHTML(`<div>
                            <div class='alert-popup-title'>${properties.name || 'Unknown'}</div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Address:</div>
                                <div class='alert-popup-row-value'>${properties.address || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>City:</div>
                                <div class='alert-popup-row-value'>${properties.city || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Zip Code:</div>
                                <div class='alert-popup-row-value'>${properties.zip_code || 'Unknown'}</div>
                            </div>
                            <div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>State</div>
                                <div class='alert-popup-row-value'>${properties.state || 'Unknown'}</div>
                            </div>
                        </div>`)
                            .addTo(map.current);
                    });
        
                    map.current.on('mouseenter', `Layer-emergency-stations-${station[0]}-symbol`, () => {
                        map.current.getCanvas().style.cursor = 'pointer';
                    });
        
                    map.current.on('mouseleave', `Layer-emergency-stations-${station[0]}-symbol`, () => {
                        map.current.getCanvas().style.cursor = '';
                    });
                });
            } catch (error) {
                console.error('Error plotting emergency stations:', error);
            }
        })
    };
   
    const value = useMemo(() => ({
        getPowerPlants,
        getEmergencyStations
    }), []);
  
    return (
        <SensorDataContext.Provider 
            value={value}
        >
        {children}
        </SensorDataContext.Provider>
    );
    };

export const useFacilitiesContext = () => useContext(SensorDataContext);
